import { appLayoutRevalidatesOnNavigation } from './should-revalidate';

/**
 * Warms the browser's HTTP cache with the route data for `page` so a
 * subsequent navigation there serves from cache.
 *
 * This deliberately avoids <link rel="prefetch"> (PrefetchPageLinks): Safari
 * ignores the tag entirely, and Chromium stores link-prefetched responses as
 * Sec-Purpose-flagged cache entries that later navigations reuse
 * inconsistently. A plain fetch primes an ordinary HTTP-cache entry instead;
 * the X-Purpose header lets the server mark the response cacheable (see
 * handleDataRequest in entry.server). The entry only lives for that
 * response's short max-age, so warm close to the navigation, not on page
 * load.
 *
 * The warmed URL must byte-match React Router's single-fetch request, whose
 * `_routes` param lists the routes that revalidate — pass the ids of those
 * routes in match order (see navigationRouteIds). If the revalidation policy
 * and the passed list ever drift apart, navigations just stop matching the
 * warmed URL — the cost is an unused background request, never a wrong
 * response.
 */
export const warmRouteData = (
    page: string,
    revalidatingRouteIds: string,
): Promise<void> => {
    const url = new URL(page, window.location.origin);
    url.pathname += '.data';
    url.searchParams.set('_routes', revalidatingRouteIds);
    return (
        fetch(url, { headers: { 'X-Purpose': 'prefetch' } })
            // Drain the body so the cache entry completes.
            .then((response) => response.arrayBuffer())
            .then(() => undefined)
            .catch(() => {
                // Cache warming only — the navigation fetches for real.
            })
    );
};

const APP_LAYOUT_ROUTE_ID = 'routes/_app';
const URL_BASE = 'https://archipro.invalid';

/**
 * The `_routes` value React Router requests for a GET navigation from `from`
 * to `page`: every route below root in match order, less the `_app` layout
 * when that navigation keeps its data. Root never revalidates. Assumes `page`
 * matches the same routes as the current page.
 */
export const navigationRouteIds = (
    matches: readonly { id: string }[],
    from: { pathname: string; search?: string },
    page: string,
): string => {
    const includeAppLayout = appLayoutRevalidatesOnNavigation(
        new URL(from.pathname + (from.search ?? ''), URL_BASE),
        new URL(page, URL_BASE),
    );
    return matches
        .slice(1)
        .map((match) => match.id)
        .filter((id) => includeAppLayout || id !== APP_LAYOUT_ROUTE_ID)
        .join(',');
};
